import * as z from "zod/v4";
import { zUsdMicros } from "../../currency";
import {
  baseBillingSettingsSchema,
  PaymentProviderType,
} from "../billing-engine-types";
import { orderTrackingBillingWindowSchema } from "../billing-window-config-definition";
import { orderTrackingOverviewSchema } from "../itemized-invoice-types";
import { billedTrackingOrderSchema } from "../tracking/billed-tracking-order-definition";

export type OrderTrackingInvoiceBillingSettings = z.infer<
  typeof orderTrackingInvoiceBillingSettingsSchema
>;
export const orderTrackingInvoiceBillingSettingsSchema =
  baseBillingSettingsSchema.extend({
    paymentProvider: z.enum(PaymentProviderType).optional(),
    perOrderSettings: z.object({
      enabled: z.boolean(),
      includedOrders: z.number(),
      overagePricePerOrderMicros: zUsdMicros,
    }),
    perSmsSettings: z.object({
      enabled: z.boolean(),
      includedCredits: z.number(),
      overagePricePerSmsMicros: zUsdMicros,
      overagePricePerMmsMicros: zUsdMicros,
      ignoreCarrierFees: z.boolean(),
    }),
    perEmailSettings: z.object({
      enabled: z.boolean(),
      includedEmails: z.number(),
      overagePricePerEmailMicros: zUsdMicros,
    }),
  });

export type OrderTrackingCurrentUsageData = z.infer<
  typeof orderTrackingCurrentUsageDataSchema
>;
export const orderTrackingCurrentUsageDataSchema = z.object({
  type: z.literal("order-tracking"),
  billPerOrderEnabled: z.boolean(),
  orderCreditsUsed: z.number(),
  orderCreditsIncluded: z.number(),
  overagePricePerOrderMicros: zUsdMicros,

  billPerEmailEnabled: z.boolean(),
  emailCreditsUsed: z.number(),
  emailCreditsIncluded: z.number(),
  overagePricePerEmailMicros: zUsdMicros,

  billPerSmsEnabled: z.boolean(),
  smsCreditsUsed: z.number(),
  smsCreditsIncluded: z.number(),
  overagePricePerSmsMicros: zUsdMicros,
  // TODO: Should we have overagePricePerMmsMicros here?
  smsCarrierFeesMicros: zUsdMicros,
});

export type CachedBilledTrackingOrder = z.infer<
  typeof cachedBilledTrackingOrderSchema
>;
const cachedBilledTrackingOrderSchema = billedTrackingOrderSchema.omit({
  _id: true,
  teamId: true,
  billingWindowConfigId: true,
});

export type OrderTrackingFinalUsage = z.infer<
  typeof orderTrackingFinalUsageSchema
>;
export const orderTrackingFinalUsageSchema = z.object({
  teamId: z.string(),
  billingWindowConfig: orderTrackingBillingWindowSchema,
  overview: orderTrackingOverviewSchema,
  trackedOrders: z.record(z.string(), cachedBilledTrackingOrderSchema),
  currentProgress: z
    .object({
      isOrderComplete: z.boolean(),
      isSmsComplete: z.boolean(),
      isEmailComplete: z.boolean(),
      cursor: z
        .object({ timestamp: z.coerce.date(), _id: z.string() })
        .optional(),
    })
    .optional(),
});
